import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Card, CardHeader, CardBody, Table } from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { POST_KPI_DASHBOARD } from "../../helpers/data_helper";
import Loader from "../../Components/Common/Loader";
import { getDateByName, convertDateFormate } from "../../helpers/api_helper";
import CustomPagination from "../../Components/Common/CustomPagination";

// const itemsPerPage = 2;

const renderTopPerformerBadges = (topPerformer) => {
  if (!topPerformer) return null;

  let list = [];
  if (Array.isArray(topPerformer)) {
    list = topPerformer;
  } else if (typeof topPerformer === "string" && topPerformer !== "") {
    list = [topPerformer];
  }

  if (list.length === 0) return null;

  list = list.filter((item) => item !== "");
  if (list.length === 0) return null;

  // Chunk list into arrays of 2 elements
  const chunks = [];
  for (let i = 0; i < list.length; i += 2) {
    chunks.push(list.slice(i, i + 2));
  }

  return (
    <div className="d-flex flex-column align-items-center">
      {chunks.map((chunk, chunkIdx) => (
        <div key={chunkIdx} className="d-flex justify-content-center mb-1">
          {chunk.map((tp, tpIdx) => {
            const displayText = tp.toLowerCase().startsWith("overall")
              ? tp
              : `${tp}`;
            return (
              <span
                key={tpIdx}
                className="badge bg-warning text-white py-2 px-3 rounded-pill fs-11 me-1 d-inline-flex align-items-center"
                style={{ whiteSpace: "nowrap" }}
              >
                <i className="ri-trophy-line me-1"></i>
                {displayText}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const parseBeveragePart = (ratioStr) => {
  if (!ratioStr || typeof ratioStr !== "string") return NaN;
  const parts = ratioStr.split(":");
  if (parts.length < 2) return NaN;
  const val = parseFloat(parts[1].trim());
  return isNaN(val) ? NaN : val;
};

const calculateItemsPerCoverTeamAvg = (dataList, mealPeriods) => {
  const avg = {
    staff: "TEAM AVG",
    isTeamAvg: true,
    topPerformer: null,
  };

  mealPeriods.forEach((mp) => {
    const vals = dataList
      .map((row) => parseFloat(row[mp.title]))
      .filter((val) => !isNaN(val) && val > 0);

    if (vals.length > 0) {
      const sum = vals.reduce((acc, val) => acc + val, 0);
      // avg[mp.title] = `${(sum / vals.length).toFixed(1)} : 1`;
      avg[mp.title] = `${(sum / vals.length).toFixed(2)}`;
    } else {
      avg[mp.title] = "-";
    }
  });

  return avg;
};

const calculateFoodToBeverageTeamAvg = (dataList, mealPeriods) => {
  const avg = {
    staff: "TEAM AVG",
    isTeamAvg: true,
    topPerformer: null,
  };

  mealPeriods.forEach((mp) => {
    const vals = dataList
      .map((row) => parseBeveragePart(row[mp.title]))
      .filter((val) => !isNaN(val));

    if (vals.length > 0) {
      const sum = vals.reduce((acc, val) => acc + val, 0);
      avg[mp.title] = `1 : ${(sum / vals.length).toFixed(2)}`;
    } else {
      avg[mp.title] = "-";
    }
  });

  return avg;
};

// const assignTopPerformers = (dataList, mealPeriods, isRatioTable = false) => {
//   if (dataList.length === 0) return dataList;
//   const getValue = (val) => {
//     if (isRatioTable) {
//       return parseBeveragePart(val);
//     } else {
//       return parseFloat(val);
//     }
//   };
//   const maxValues = {};
//   mealPeriods.forEach((mp) => {
//     maxValues[mp.title] = -1;
//   });
//   dataList.forEach((row) => {
//     mealPeriods.forEach((mp) => {
//       const val = getValue(row[mp.title]);
//       if (!isNaN(val) && val > maxValues[mp.title]) {
//         maxValues[mp.title] = val;
//       }
//     });
//   });
//   return dataList.map((row) => {
//     let tags = [];
//     mealPeriods.forEach((mp) => {
//       const val = getValue(row[mp.title]);
//       if (!isNaN(val) && val === maxValues[mp.title] && val > 0) {
//         tags.push(mp.title);
//       }
//     });
//     let topPerformer = null;
//     if (tags.length === mealPeriods.length && mealPeriods.length > 0) {
//       topPerformer = "Overall";
//     } else if (tags.length > 0) {
//       topPerformer = tags.join(" & ");
//     }
//     return {
//       ...row,
//       topPerformer,
//     };
//   });
// };

const ServerRatiosTable = ({ activeDate, customDate, mealPeriod, selectedPageItems, setSelectedPageItems, selectedPageRatio, setSelectedPageRatio }) => {
  const [itemsPerCoverData, setItemsPerCoverData] = useState([]);
  const [foodToBeverageData, setFoodToBeverageData] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingRatio, setLoadingRatio] = useState(false);

  // Pagination states
  // const [selectedPageItems, setSelectedPageItems] = useState(0);
  const [pageCountItems, setPageCountItems] = useState(0);

  // const [selectedPageRatio, setSelectedPageRatio] = useState(0);
  const [pageCountRatio, setPageCountRatio] = useState(0);

  const { mealPeriods } = useSelector((state) => ({
    mealPeriods: state.Dashboard.filterData.mealperiod || [],
  }));

  // Reset page numbers when main filters change
  useEffect(() => {
    setSelectedPageItems(0);
    setSelectedPageRatio(0);
    setItemsPerCoverData([]);
    setFoodToBeverageData([]);
  }, [activeDate, customDate, mealPeriod, setSelectedPageItems, setSelectedPageRatio]);

  // Items per cover effect
  useEffect(() => {
    if (
      activeDate.value === "Custom" &&
      (!customDate || !customDate[0] || !customDate[1])
    ) {
      return;
    }

    let active = true;
    const controller = new AbortController();
    setLoadingItems(true);

    const dateFilter =
      activeDate.value === "Custom"
        ? {
            from: convertDateFormate(customDate[0]),
            to: convertDateFormate(customDate[1]),
          }
        : getDateByName(activeDate.value);

    postDataAPI(
      {
        dateFilter: dateFilter,
        mealPeriod: mealPeriod,
        page: selectedPageItems,
        // limit: itemsPerPage,
        serviceName: POST_KPI_DASHBOARD.serviceName,
        op: POST_KPI_DASHBOARD.fetch_items_per_cover_op,
      },
      controller.signal,
    )
      .then((response) => {
        if (!active) return;
        setLoadingItems(false);
        if (response.status === "ok") {
          let servers = [];
          if (response.data && typeof response.data === "object") {
            const resData = response.data.data !== undefined ? response.data.data : response.data;
            servers = Object.values(resData);
          }
          const mapped = servers.map((row) => {
            const serverRow = {
              staff: row.staff || "",
              topPerformer: row.top_performer || [],
            };
            mealPeriods.forEach((mp) => {
              const val = row[mp.title];
              serverRow[mp.title] =
                val !== undefined && val !== null && val !== "" ? val : "-";
            });
            return serverRow;
          });

          const dataWithTrophy = mapped;
          if (dataWithTrophy.length > 0) {
            const teamAvg = calculateItemsPerCoverTeamAvg(
              dataWithTrophy,
              mealPeriods,
            );
            setItemsPerCoverData([teamAvg, ...dataWithTrophy]);
          } else {
            setItemsPerCoverData([]);
          }

          const totalRecords = response.data.totalData !== undefined ? response.data.totalData : servers.length;
          setPageCountItems(Math.ceil(totalRecords / response.data.limit));
        } else {
          setItemsPerCoverData([]);
          setPageCountItems(0);
        }
      })
      .catch((err) => {
        if (!active) return;
        if (
          err.code === "ERR_CANCELED" ||
          err.name === "CanceledError" ||
          err.name === "AbortError"
        ) {
          return;
        }
        setLoadingItems(false);
        setPageCountItems(0);
        console.error(err);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [activeDate, customDate, mealPeriod, mealPeriods, selectedPageItems]);

  // Food to beverage ratio effect
  useEffect(() => {
    if (
      activeDate.value === "Custom" &&
      (!customDate || !customDate[0] || !customDate[1])
    ) {
      return;
    }

    let active = true;
    const controller = new AbortController();
    setLoadingRatio(true);

    const dateFilter =
      activeDate.value === "Custom"
        ? {
            from: convertDateFormate(customDate[0]),
            to: convertDateFormate(customDate[1]),
          }
        : getDateByName(activeDate.value);

    postDataAPI(
      {
        dateFilter: dateFilter,
        mealPeriod: mealPeriod,
        page: selectedPageRatio,
        // limit: itemsPerPage,
        serviceName: POST_KPI_DASHBOARD.serviceName,
        op: POST_KPI_DASHBOARD.fetch_food_to_beverage_ratio_op,
      },
      controller.signal,
    )
      .then((response) => {
        if (!active) return;
        setLoadingRatio(false);
        if (response.status === "ok") {
          let servers = [];
          if (response.data && typeof response.data === "object") {
            const resData = response.data.data !== undefined ? response.data.data : response.data;
            servers = Object.values(resData);
          }
          const mapped = servers.map((row) => {
            const serverRow = {
              staff: row.staff || "",
              topPerformer: row.top_performer || [],
            };
            mealPeriods.forEach((mp) => {
              const val = row[mp.title];
              serverRow[mp.title] =
                val !== undefined && val !== null && val !== "" ? val : "-";
            });
            return serverRow;
          });

          const dataWithTrophy = mapped;
          if (dataWithTrophy.length > 0) {
            const teamAvg = calculateFoodToBeverageTeamAvg(
              dataWithTrophy,
              mealPeriods,
            );
            setFoodToBeverageData([teamAvg, ...dataWithTrophy]);
          } else {
            setFoodToBeverageData([]);
          }

          const totalRecords = response.data.totalData !== undefined ? response.data.totalData : servers.length;
          setPageCountRatio(Math.ceil(totalRecords / response.data.limit));
        } else {
          setFoodToBeverageData([]);
          setPageCountRatio(0);
        }
      })
      .catch((err) => {
        if (!active) return;
        if (
          err.code === "ERR_CANCELED" ||
          err.name === "CanceledError" ||
          err.name === "AbortError"
        ) {
          return;
        }
        setLoadingRatio(false);
        setPageCountRatio(0);
        console.error(err);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [activeDate, customDate, mealPeriod, mealPeriods, selectedPageRatio]);

  const formatItemsPerCover = (val) => {
    if (val === "" || val === null || val === undefined || val === "-")
      return "-";
    if (typeof val === "string" && val.includes(":")) return val;
    const num = val;
    if (isNaN(num)) return val;
    return `${num}`;
  };

  const formatFoodToBeverage = (val) => {
    if (
      val === "" ||
      val === null ||
      val === undefined ||
      val === "-" ||
      val === "0 : 0" ||
      val === "0:0"
    )
      return "-";

    if (typeof val === "string" && (val.includes("1:") || val.includes("1 : ")))
      return val.replace("1 :", "");

    return val;
  };

  return (
    <Card className="mb-4">
      <CardHeader className="align-items-center d-flex">
        <div className="flex-grow-1">
          <h4 className="card-title mb-1 text-dark">
            Server Ratios by Meal Period
          </h4>
          <p className="text-muted mb-0 fs-13">
            Items per cover and food-to-beverage ratios across meal periods,
            with the team average and top performer highlighted.
          </p>
        </div>
      </CardHeader>

      <CardBody className="p-4">
        {/* 1. Items per Cover Section */}
        <div className="mb-5 position-relative">
          <div className="d-flex align-items-center mb-3">
            <div
              className="icon-box rounded bg-primary text-white d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              <i className="ri-restaurant-2-line fs-20"></i>
            </div>
            <div className="ps-3">
              <h5 className="mb-0 fs-16 fw-semibold text-dark">
                Items per Cover
              </h5>
              <p className="text-muted mb-0 fs-12">
                Average items sold per guest
              </p>
            </div>
          </div>

          {loadingItems && <Loader />}

          <div
            className={`table-responsive table-card mt-3 ${
              loadingItems ? "opacity-25" : ""
            }`}
          >
            <Table className="table align-middle table-nowrap mb-0">
              <thead className="table-light text-muted">
                <tr>
                  <th scope="col" style={{ width: "20%" }}>
                    Staff
                  </th>
                  {mealPeriods.map((mp) => (
                    <th
                      key={mp.sp_id}
                      scope="col"
                      className="text-center"
                      style={{
                        width: `${60 / Math.max(1, mealPeriods.length)}%`,
                      }}
                    >
                      {mp.title}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="text-center"
                    style={{ width: "20%" }}
                  >
                    Top Performer
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loadingItems && itemsPerCoverData.length === 0 && (
                  <tr>
                    <td
                      colSpan={mealPeriods.length + 2}
                      className="text-center text-muted py-4"
                    >
                      No data available for the selected filters.
                    </td>
                  </tr>
                )}
                {itemsPerCoverData.map((row, idx) => (
                  <tr key={idx} className={row.isTeamAvg ? "fw-bold" : ""}>
                    <td
                      className={
                        row.isTeamAvg
                          ? "fw-bold text-dark fs-14"
                          : "text-muted fs-14"
                      }
                    >
                      {row.staff}
                    </td>
                    {mealPeriods.map((mp) => (
                      <td key={mp.sp_id} className="text-center">
                        <span
                          className={
                            row.isTeamAvg
                              ? "text-dark fw-bold"
                              : "text-black fw-medium"
                          }
                        >
                          {formatItemsPerCover(row[mp.title])}
                        </span>
                      </td>
                    ))}
                    <td className="text-center">
                      {renderTopPerformerBadges(row.topPerformer)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {pageCountItems > 1 && (
            <div className="mt-4 d-flex justify-content-end">
              <CustomPagination
                pageCount={pageCountItems}
                setSelectedPage={setSelectedPageItems}
              />
            </div>
          )}
        </div>

        <hr className="my-4 text-muted opacity-25" />

        {/* 2. Food to Beverage Ratio Section */}
        <div className="position-relative">
          <div className="d-flex align-items-center mb-3">
            <div
              className="icon-box rounded bg-danger text-white d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              <i className="ri-cup-line fs-20"></i>
            </div>
            <div className="ps-3">
              <h5 className="mb-0 fs-16 fw-semibold text-dark">
                Food to Beverage Ratio
              </h5>
              <p className="text-muted mb-0 fs-12">
                Beverages sold per food item
              </p>
            </div>
          </div>

          {loadingRatio && <Loader />}

          <div
            className={`table-responsive table-card mt-3 ${
              loadingRatio ? "opacity-25" : ""
            }`}
          >
            <Table className="table align-middle table-nowrap mb-0">
              <thead className="table-light text-muted">
                <tr>
                  <th scope="col" style={{ width: "20%" }}>
                    Staff
                  </th>
                  {mealPeriods.map((mp) => (
                    <th
                      key={mp.sp_id}
                      scope="col"
                      className="text-center"
                      style={{
                        width: `${60 / Math.max(1, mealPeriods.length)}%`,
                      }}
                    >
                      {mp.title}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="text-center"
                    style={{ width: "20%" }}
                  >
                    Top Performer
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loadingRatio && foodToBeverageData.length === 0 && (
                  <tr>
                    <td
                      colSpan={mealPeriods.length + 2}
                      className="text-center text-muted py-4"
                    >
                      No data available for the selected filters.
                    </td>
                  </tr>
                )}
                {foodToBeverageData.map((row, idx) => (
                  <tr key={idx} className={row.isTeamAvg ? "fw-bold" : ""}>
                    <td
                      className={
                        row.isTeamAvg
                          ? "fw-bold text-dark fs-14"
                          : "text-muted fs-14"
                      }
                    >
                      {row.staff}
                    </td>
                    {mealPeriods.map((mp) => (
                      <td key={mp.sp_id} className="text-center">
                        <span
                          className={
                            row.isTeamAvg
                              ? "text-dark fw-bold"
                              : "text-black fw-medium"
                          }
                        >
                          {formatFoodToBeverage(row[mp.title])}
                        </span>
                      </td>
                    ))}
                    <td className="text-center">
                      {renderTopPerformerBadges(row.topPerformer)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {pageCountRatio > 1 && (
            <div className="mt-4 d-flex justify-content-end">
              <CustomPagination
                pageCount={pageCountRatio}
                setSelectedPage={setSelectedPageRatio}
              />
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default ServerRatiosTable;
